/**
 * TG-6 real client for TG-2's live assignment endpoints (`api.functions.
 * assignments`): POST /api/portal/assignments, .../{id}/reassign and
 * .../{id}/take-back. Unlike `RenewalSummaryPort`/`WorkingCopyPort` (this
 * feature's own self-authored, stub-backed ports — ADR-20261015-bzyr),
 * this feature's own TG-2 already built and tested these three endpoints,
 * so TG-6 calls them for real through `authenticatedFetch`, the same
 * session-aware wrapper `submitRenewal` already uses — never a second,
 * ad-hoc `fetch`.
 *
 * `{id}` in the two sub-routes is always a `sectionAssigneeId`
 * (assignments.py's own doc comment), returned by a prior `createAssignment`
 * or `reassignAssignment` call and carried by the caller for the lifetime
 * of that colleague's assignment — this client reads no assignment list
 * of its own (no such read endpoint exists yet; the caller tracks the
 * assignment state it has itself created, per this task group's own
 * documented scope decision in `features/renewal-capture/assignment-state.ts`).
 */
import { authenticatedFetch } from "./authenticated-fetch";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

export type AssignmentApiErrorCode =
  | "invalid_request"
  | "section_already_assigned"
  | "owner_capability_required"
  | "assignee_unchanged"
  | "section_not_assigned"
  | "not_found"
  /** m1-portal-ux-fixes Fix D: the shared 409 `src/api/submission_status.py`'s
   * `evaluate_submission_lock` returns for every write route once the
   * renewal is submitted (create/reassign/take-back assignment alike). */
  | "renewal_submitted"
  | "unknown";

export interface AssignmentApiError {
  readonly code: AssignmentApiErrorCode;
  readonly message: string;
}

export type AssignmentApiResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: AssignmentApiError };

export interface CreateAssignmentRequest {
  readonly sectionIds: readonly string[];
  readonly assigneeName: string;
  readonly assigneeEmail: string;
}

export interface AssignmentCreated {
  readonly sectionAssigneeId: number;
  readonly sectionIds: readonly string[];
}

export interface ReassignRequest {
  readonly currentSectionAssigneeId: number;
  readonly assigneeName: string;
  readonly assigneeEmail: string;
  /** Omitted: every section currently held by the current assignee. */
  readonly sectionIds?: readonly string[];
}

export interface TakeBackRequest {
  readonly sectionAssigneeId: number;
  /** Omitted: every section currently held by this assignee. */
  readonly sectionIds?: readonly string[];
}

function isErrorCode(value: unknown): value is AssignmentApiErrorCode {
  return (
    typeof value === "string" &&
    (
      [
        "invalid_request",
        "section_already_assigned",
        "owner_capability_required",
        "assignee_unchanged",
        "section_not_assigned",
        "not_found",
        "renewal_submitted",
      ] as readonly string[]
    ).includes(value)
  );
}

async function parseError(response: Response): Promise<AssignmentApiError> {
  try {
    const body: unknown = await response.json();
    const code = typeof body === "object" && body !== null ? (body as Record<string, unknown>).code : undefined;
    const message = typeof body === "object" && body !== null ? (body as Record<string, unknown>).message : undefined;
    return {
      code: isErrorCode(code) ? code : "unknown",
      message: typeof message === "string" ? message : "Something went wrong. Try again.",
    };
  } catch {
    return { code: "unknown", message: "Something went wrong. Try again." };
  }
}

export async function createAssignment(request: CreateAssignmentRequest): Promise<AssignmentApiResult<AssignmentCreated>> {
  let response: Response;
  try {
    response = await authenticatedFetch("/api/portal/assignments", {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({
        sectionIds: request.sectionIds,
        assigneeName: request.assigneeName,
        assigneeEmail: request.assigneeEmail,
      }),
    });
  } catch {
    return { ok: false, error: { code: "unknown", message: "Something went wrong. Try again." } };
  }
  if (response.status === 201) {
    const body = (await response.json()) as { sectionAssigneeId: number; sectionIds: string[] };
    return { ok: true, value: { sectionAssigneeId: body.sectionAssigneeId, sectionIds: body.sectionIds } };
  }
  return { ok: false, error: await parseError(response) };
}

export async function reassignAssignment(request: ReassignRequest): Promise<AssignmentApiResult<AssignmentCreated>> {
  let response: Response;
  try {
    response = await authenticatedFetch(
      `/api/portal/assignments/${encodeURIComponent(String(request.currentSectionAssigneeId))}/reassign`,
      {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify({
          assigneeName: request.assigneeName,
          assigneeEmail: request.assigneeEmail,
          ...(request.sectionIds !== undefined ? { sectionIds: request.sectionIds } : {}),
        }),
      },
    );
  } catch {
    return { ok: false, error: { code: "unknown", message: "Something went wrong. Try again." } };
  }
  if (response.status === 200) {
    const body = (await response.json()) as { sectionAssigneeId: number; sectionIds: string[] };
    return { ok: true, value: { sectionAssigneeId: body.sectionAssigneeId, sectionIds: body.sectionIds } };
  }
  return { ok: false, error: await parseError(response) };
}

export interface TakeBackResult {
  readonly sectionAssigneeId: number;
  readonly sectionsRevokedCount: number;
}

export type SectionAssignmentState = "assigned" | "completed_by_colleague";

export interface SectionAssignmentStatus {
  readonly sectionId: string;
  readonly sectionAssigneeId: number;
  readonly assigneeName: string;
  readonly assigneeEmail: string;
  readonly state: SectionAssignmentState;
}

/**
 * GET /api/portal/assignments (TG-6, AC-14): every currently-active
 * (non-revoked) section delegation on the owner's own renewal, added
 * alongside TG-2's three write routes (which this feature's section list
 * is the first owner-facing screen to need a read for). Carries the
 * colleague's email address as well as their name (ADR-20261016-k3pd) so
 * the Reassign dialog's intro line can name them the same way H2 does.
 */
export async function listAssignments(): Promise<AssignmentApiResult<readonly SectionAssignmentStatus[]>> {
  let response: Response;
  try {
    response = await authenticatedFetch("/api/portal/assignments", { method: "GET" });
  } catch {
    return { ok: false, error: { code: "unknown", message: "Something went wrong. Try again." } };
  }
  if (response.status === 200) {
    const body = (await response.json()) as { assignments: SectionAssignmentStatus[] };
    return { ok: true, value: body.assignments };
  }
  return { ok: false, error: await parseError(response) };
}

export async function takeBackAssignment(request: TakeBackRequest): Promise<AssignmentApiResult<TakeBackResult>> {
  let response: Response;
  try {
    response = await authenticatedFetch(
      `/api/portal/assignments/${encodeURIComponent(String(request.sectionAssigneeId))}/take-back`,
      {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(request.sectionIds !== undefined ? { sectionIds: request.sectionIds } : {}),
      },
    );
  } catch {
    return { ok: false, error: { code: "unknown", message: "Something went wrong. Try again." } };
  }
  if (response.status === 200) {
    const body = (await response.json()) as { sectionAssigneeId: number; sectionsRevokedCount: number };
    return {
      ok: true,
      value: { sectionAssigneeId: body.sectionAssigneeId, sectionsRevokedCount: body.sectionsRevokedCount },
    };
  }
  return { ok: false, error: await parseError(response) };
}
